import React, { useState, useMemo } from 'react';
import { ArrowLeft, ArrowUpRight, ArrowDownLeft, Calendar, FileText, Filter, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import { DatePicker } from '../../components/UI/DatePicker';

// Type Definition
interface FinancialRecord {
    id: string;
    contractId: string;
    title: string;
    role: 'creator' | 'client'; // 'creator' = Income, 'client' = Expenditure
    amount: number;
    date: string; // Completion or Termination Date
    status: 'completed' | 'terminated';
    paymentScheme: 'one-off' | 'installments'; // 一次付清 / 多次付清
    recordType: 'full' | 'down_payment'; // 全款 (Completed) / 首款 (Terminated)
    method: '支付宝' | '微信支付' | '银行转账';
}

// Mock Data
const FINANCIAL_RECORDS: FinancialRecord[] = [
    {
        id: 'REC-001',
        contractId: 'CT-2024001',
        title: 'Sora 商业视频定制 - 宣传片 (尾款)',
        role: 'creator',
        amount: 10000,
        date: '2024-03-20',
        status: 'completed',
        paymentScheme: 'installments',
        recordType: 'full',
        method: '支付宝'
    },
    {
        id: 'REC-006',
        contractId: 'CT-2024001',
        title: 'Sora 商业视频定制 - 宣传片 (首付款)',
        role: 'creator',
        amount: 5000,
        date: '2024-03-01',
        status: 'completed',
        paymentScheme: 'installments',
        recordType: 'down_payment',
        method: '银行转账'
    },
    {
        id: 'REC-002',
        contractId: 'CT-2024005',
        title: '品牌 Logo 设计 - 极简风格',
        role: 'client', // Expenditure
        amount: 8000,
        date: '2024-03-18',
        status: 'completed',
        paymentScheme: 'one-off',
        recordType: 'full',
        method: '微信支付'
    },
    {
        id: 'REC-003',
        contractId: 'CT-2024008',
        title: '电商详情页 AI 模特生成',
        role: 'creator',
        amount: 2500,
        date: '2024-03-15',
        status: 'terminated',
        paymentScheme: 'installments',
        recordType: 'down_payment', // Only down payment since it was terminated
        method: '支付宝'
    },
    {
        id: 'REC-004',
        contractId: 'CT-2024012',
        title: '游戏角色原画设定',
        role: 'client',
        amount: 12000,
        date: '2024-02-28',
        status: 'completed',
        paymentScheme: 'installments', // Paid in milestones but completed
        recordType: 'full',
        method: '银行转账'
    },
    {
        id: 'REC-005',
        contractId: 'CT-2024015',
        title: '产品包装 3D 渲染',
        role: 'creator',
        amount: 4500,
        date: '2024-02-10',
        status: 'completed',
        paymentScheme: 'one-off',
        recordType: 'full',
        method: '微信支付'
    },
];

const TransactionsPage = () => {
    // Filter States
    const [showFilters, setShowFilters] = useState(false);
    const defaultFilters = useMemo(() => ({
        dateRange: { start: '', end: '' },
        amountRange: { min: '', max: '' },
        role: 'all' as 'all' | 'income' | 'expenditure',
        recordType: 'all' as 'all' | 'full' | 'down_payment',
        paymentScheme: 'all' as 'all' | 'one-off' | 'installments'
    }), []);

    const [draftFilters, setDraftFilters] = useState(defaultFilters);
    const [appliedFilters, setAppliedFilters] = useState(defaultFilters);
    const [activeCard, setActiveCard] = useState<'income' | 'expenditure' | 'contracts' | null>(null);

    // Calculations
    const totalIncome = FINANCIAL_RECORDS
        .filter(r => r.role === 'creator')
        .reduce((sum, r) => sum + r.amount, 0);

    const totalExpenditure = FINANCIAL_RECORDS
        .filter(r => r.role === 'client')
        .reduce((sum, r) => sum + r.amount, 0);

    // Filter Logic
    const filteredRecords = useMemo(() => {
        return FINANCIAL_RECORDS.filter(record => {
            // Role Filter
            if (appliedFilters.role === 'income' && record.role !== 'creator') return false;
            if (appliedFilters.role === 'expenditure' && record.role !== 'client') return false;

            // Record Type Filter (Full vs Down Payment)
            if (appliedFilters.recordType !== 'all' && record.recordType !== appliedFilters.recordType) return false;

            // Payment Scheme Filter (One-off vs Installments)
            if (appliedFilters.paymentScheme !== 'all' && record.paymentScheme !== appliedFilters.paymentScheme) return false;

            // Date Range Filter
            if (appliedFilters.dateRange.start && new Date(record.date) < new Date(appliedFilters.dateRange.start)) return false;
            if (appliedFilters.dateRange.end && new Date(record.date) > new Date(appliedFilters.dateRange.end)) return false;

            // Amount Range Filter
            if (appliedFilters.amountRange.min && record.amount < Number(appliedFilters.amountRange.min)) return false;
            if (appliedFilters.amountRange.max && record.amount > Number(appliedFilters.amountRange.max)) return false;

            return true;
        });
    }, [appliedFilters]);

    const displayRecords = useMemo(() => {
        let records = filteredRecords;
        if (activeCard === 'income') {
            records = records.filter(r => r.role === 'creator');
        } else if (activeCard === 'expenditure') {
            records = records.filter(r => r.role === 'client');
        }

        return [...records].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    }, [filteredRecords, activeCard]);

    const activeGroupedRecords = useMemo(() => {
        if (activeCard !== 'contracts') return null;
        const groups: Record<string, FinancialRecord[]> = {};
        const groupList: { contractId: string; records: FinancialRecord[] }[] = [];
        displayRecords.forEach(record => {
            if (!groups[record.contractId]) {
                groups[record.contractId] = [];
                groupList.push({ contractId: record.contractId, records: groups[record.contractId] });
            }
            groups[record.contractId].push(record);
        });
        return groupList;
    }, [displayRecords, activeCard]);

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('zh-CN', { style: 'currency', currency: 'CNY' }).format(amount);
    };

    const renderRecord = (record: FinancialRecord) => (
        <div key={record.id} className="p-6 hover:bg-gray-50 transition-colors group">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                {/* Left: Info */}
                <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${record.recordType === 'full' ? 'bg-blue-50 text-blue-600' : 'bg-orange-50 text-orange-600'}`}>
                        {record.recordType === 'full' ? <FileText className="w-5 h-5" /> : <ArrowUpRight className="w-5 h-5 rotate-45" />}
                    </div>
                    <div>
                        <div className="flex items-center gap-2 mb-0.5">
                            <span className="font-bold text-gray-900">{record.title}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded border ${record.role === 'creator'
                                ? 'bg-green-50 border-green-100 text-green-700'
                                : 'bg-red-50 border-red-100 text-red-700'
                                }`}>
                                {record.role === 'creator' ? '收入' : '支出'}
                            </span>
                        </div>
                        <div className="text-sm text-gray-500 flex flex-wrap items-center gap-x-3 gap-y-1">
                            <span>{record.date}</span>
                            <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                            <span>{record.method}</span>
                            <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                            <span>{record.paymentScheme === 'one-off' ? '一次付清' : '分期支付'}</span>
                            <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                            <span>{record.recordType === 'full' ? '项目全款' : '仅首付款(已终止)'}</span>
                        </div>
                        <div className="mt-2 text-xs text-gray-400 font-mono">
                            合同ID: {record.contractId}
                        </div>
                    </div>
                </div>

                {/* Right: Amount & Status */}
                <div className="text-left md:text-right shrink-0">
                    <div className={`text-lg font-bold ${record.role === 'creator' ? 'text-green-600' : 'text-gray-900'}`}>
                        {record.role === 'creator' ? '+' : '-'}{formatCurrency(record.amount)}
                    </div>
                    <div className="text-xs font-medium mt-1 flex items-center md:justify-end gap-1.5">
                        <span className={`w-1.5 h-1.5 rounded-full ${record.status === 'completed' ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                        <span className={record.status === 'completed' ? 'text-green-600' : 'text-gray-500'}>
                            {record.status === 'completed' ? '已完成' : '已终止'}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50 pt-24 pb-20">
            <div className="max-w-[1000px] mx-auto px-6">

                {/* Header */}
                <div className="flex items-center gap-4 mb-8">
                    <Link to="/profile" className="w-10 h-10 rounded-full bg-white flex items-center justify-center hover:bg-gray-100 transition-colors shadow-sm">
                        <ArrowLeft className="w-5 h-5 text-gray-600" />
                    </Link>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">交易记录</h1>
                        <p className="text-sm text-gray-500 mt-1">查看所有已完成或终止项目的财务明细</p>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    {/* Income Card */}
                    <div
                        onClick={() => setActiveCard(activeCard === 'income' ? null : 'income')}
                        className={`p-6 rounded-2xl cursor-pointer transition-all transform hover:-translate-y-1 ${activeCard === 'income'
                            ? 'bg-black text-white ring-2 ring-gray-900 ring-offset-2'
                            : 'bg-white border border-gray-100 shadow-sm hover:shadow-md'
                            }`}
                    >
                        <div className="flex items-center gap-2 mb-4">
                            <ArrowDownLeft className={`w-5 h-5 ${activeCard === 'income' ? 'text-green-400' : 'text-green-500'}`} />
                            <span className={`text-xs font-bold uppercase tracking-wider ${activeCard === 'income' ? 'text-white/70' : 'text-gray-500'}`}>累计收入</span>
                        </div>
                        <div className={`text-3xl font-bold ${activeCard === 'income' ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(totalIncome)}</div>
                        <div className={`text-xs mt-2 ${activeCard === 'income' ? 'text-white/70' : 'text-gray-400'}`}>基于已完成/终止结算的项目</div>
                    </div>

                    {/* Expenditure Card */}
                    <div
                        onClick={() => setActiveCard(activeCard === 'expenditure' ? null : 'expenditure')}
                        className={`p-6 rounded-2xl cursor-pointer transition-all transform hover:-translate-y-1 ${activeCard === 'expenditure'
                            ? 'bg-black text-white ring-2 ring-gray-900 ring-offset-2 border-transparent'
                            : 'bg-white border border-gray-100 shadow-sm hover:shadow-md'
                            }`}
                    >
                        <div className="flex items-center gap-2 mb-4">
                            <ArrowUpRight className={`w-5 h-5 ${activeCard === 'expenditure' ? 'text-red-400' : 'text-red-500'}`} />
                            <span className={`text-xs font-bold uppercase tracking-wider ${activeCard === 'expenditure' ? 'text-white/70' : 'text-gray-500'}`}>累计支出</span>
                        </div>
                        <div className={`text-3xl font-bold ${activeCard === 'expenditure' ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(totalExpenditure)}</div>
                        <div className={`text-xs mt-2 ${activeCard === 'expenditure' ? 'text-white/70' : 'text-gray-400'}`}>作为需求方的项目支出</div>
                    </div>

                    {/* Count Card */}
                    <div
                        onClick={() => setActiveCard(activeCard === 'contracts' ? null : 'contracts')}
                        className={`p-6 rounded-2xl cursor-pointer transition-all transform hover:-translate-y-1 flex flex-col justify-center ${activeCard === 'contracts'
                            ? 'bg-black text-white ring-2 ring-gray-900 ring-offset-2 border-transparent'
                            : 'bg-white border border-gray-100 shadow-sm hover:shadow-md'
                            }`}
                    >
                        <div className="flex items-center gap-3 mb-2">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${activeCard === 'contracts' ? 'bg-white/20' : 'bg-blue-50'}`}>
                                <FileText className={`w-4 h-4 ${activeCard === 'contracts' ? 'text-white' : 'text-blue-600'}`} />
                            </div>
                            <span className={`text-sm font-medium ${activeCard === 'contracts' ? 'text-white/70' : 'text-gray-500'}`}>涉及合同总数</span>
                        </div>
                        <div className={`text-2xl font-bold ml-1 ${activeCard === 'contracts' ? 'text-white' : 'text-gray-900'}`}>{new Set(FINANCIAL_RECORDS.map(r => r.contractId)).size} 份</div>
                    </div>
                </div>

                {/* Transaction List */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                    <div className="px-6 py-5 border-b border-gray-100 flex justify-between items-center bg-gray-50/30">
                        <h2 className="font-bold text-gray-900">财务明细</h2>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setShowFilters(!showFilters)}
                                className={`px-3 py-1.5 text-xs font-medium rounded-lg flex items-center gap-2 transition-colors ${showFilters ? 'bg-black text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                            >
                                <Filter className="w-3.5 h-3.5" />
                                筛选
                            </button>
                            <button className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg flex items-center gap-2 transition-colors">
                                <Download className="w-3.5 h-3.5" />
                                导出
                            </button>
                        </div>
                    </div>

                    {/* Filters Panel */}
                    {showFilters && (
                        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 animate-fade-in text-sm">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                {/* Role Filter */}
                                <div className="space-y-1.5">
                                    <label className="text-xs font-medium text-gray-500">交易类型</label>
                                    <select
                                        value={draftFilters.role}
                                        onChange={(e) => setDraftFilters(prev => ({ ...prev, role: e.target.value as any }))}
                                        className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/5"
                                    >
                                        <option value="all">全部</option>
                                        <option value="income">收入 (作为创作者)</option>
                                        <option value="expenditure">支出 (作为需求方)</option>
                                    </select>
                                </div>

                                {/* Record Type Filter */}
                                <div className="space-y-1.5">
                                    <label className="text-xs font-medium text-gray-500">款项性质</label>
                                    <select
                                        value={draftFilters.recordType}
                                        onChange={(e) => setDraftFilters(prev => ({ ...prev, recordType: e.target.value as any }))}
                                        className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/5"
                                    >
                                        <option value="all">全部</option>
                                        <option value="full">项目全款 (已完成)</option>
                                        <option value="down_payment">仅首付款 (已终止)</option>
                                    </select>
                                </div>

                                {/* Payment Scheme Filter */}
                                <div className="space-y-1.5">
                                    <label className="text-xs font-medium text-gray-500">支付方式</label>
                                    <select
                                        value={draftFilters.paymentScheme}
                                        onChange={(e) => setDraftFilters(prev => ({ ...prev, paymentScheme: e.target.value as any }))}
                                        className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/5"
                                    >
                                        <option value="all">全部</option>
                                        <option value="one-off">一次付清</option>
                                        <option value="installments">分期支付</option>
                                    </select>
                                </div>

                                {/* Amount Range */}
                                <div className="space-y-1.5">
                                    <label className="text-xs font-medium text-gray-500">金额范围</label>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="number"
                                            placeholder="最小"
                                            value={draftFilters.amountRange.min}
                                            onChange={(e) => setDraftFilters(prev => ({ ...prev, amountRange: { ...prev.amountRange, min: e.target.value } }))}
                                            className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/5"
                                        />
                                        <span className="text-gray-400">-</span>
                                        <input
                                            type="number"
                                            placeholder="最大"
                                            value={draftFilters.amountRange.max}
                                            onChange={(e) => setDraftFilters(prev => ({ ...prev, amountRange: { ...prev.amountRange, max: e.target.value } }))}
                                            className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/5"
                                        />
                                    </div>
                                </div>

                                {/* Date Range */}
                                <div className="col-span-full md:col-span-2 space-y-1.5 relative">
                                    <label className="text-xs font-medium text-gray-500">日期范围</label>
                                    <div className="flex items-center gap-2">
                                        <div className="relative w-full">
                                            <DatePicker
                                                value={draftFilters.dateRange.start}
                                                onChange={(val) => setDraftFilters(prev => ({ ...prev, dateRange: { ...prev.dateRange, start: val } }))}
                                                className="w-full px-3 py-1.5 bg-white border border-gray-200 rounded-lg focus-within:ring-2 focus-within:ring-black/5 flex items-center h-[38px]"
                                                position="left"
                                            />
                                        </div>
                                        <span className="text-gray-400">至</span>
                                        <div className="relative w-full">
                                            <DatePicker
                                                value={draftFilters.dateRange.end}
                                                onChange={(val) => setDraftFilters(prev => ({ ...prev, dateRange: { ...prev.dateRange, end: val } }))}
                                                className="w-full px-3 py-1.5 bg-white border border-gray-200 rounded-lg focus-within:ring-2 focus-within:ring-black/5 flex items-center h-[38px]"
                                                position="right"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Filter Actions */}
                                <div className="col-span-full md:col-span-2 flex justify-end items-end gap-4 mt-2 lg:mt-0">
                                    <button
                                        onClick={() => {
                                            setDraftFilters(defaultFilters);
                                            setAppliedFilters(defaultFilters);
                                        }}
                                        className="text-xs text-gray-500 hover:text-black hover:underline mb-2"
                                    >
                                        重置筛选条件
                                    </button>
                                    <button
                                        onClick={() => {
                                            setAppliedFilters(draftFilters);
                                            setShowFilters(false);
                                        }}
                                        className="px-4 py-2 text-xs font-medium bg-black text-white hover:bg-gray-900 rounded-lg transition-colors shadow-sm"
                                    >
                                        应用筛选
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="divide-y divide-gray-50">
                        {displayRecords.length > 0 ? (
                            activeCard === 'contracts' && activeGroupedRecords ? (
                                activeGroupedRecords.map(group => (
                                    <div key={group.contractId} className="border-b border-gray-100 last:border-0 hover:bg-gray-50/30 transition-colors">
                                        <div className="px-6 py-3 bg-gray-50/80 border-b border-gray-100 text-sm font-medium text-gray-700 font-mono flex items-center gap-2">
                                            <FileText className="w-4 h-4 text-gray-400" />
                                            合同: {group.contractId}
                                        </div>
                                        <div className="divide-y divide-gray-50">
                                            {group.records.map(renderRecord)}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                displayRecords.map(renderRecord)
                            )
                        ) : (
                            <div className="py-20 text-center">
                                <FileText className="w-12 h-12 text-gray-200 mx-auto mb-4" />
                                <h3 className="text-lg font-medium text-gray-900">暂无相关记录</h3>
                                <p className="text-gray-500 text-sm mt-1">请尝试调整筛选条件</p>
                            </div>
                        )}
                    </div>

                    <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50 text-center">
                        <span className="text-xs text-gray-400">仅展示已结算的最终项目款项</span>
                    </div>
                </div>

                <div className="mt-8 p-4 bg-blue-50 rounded-xl border border-blue-100 flex items-start gap-3">
                    <div className="p-2 bg-blue-100 text-blue-600 rounded-full mt-0.5">
                        <ArrowUpRight className="w-4 h-4" />
                    </div>
                    <div>
                        <h4 className="text-sm font-bold text-blue-900 mb-1">财务说明</h4>
                        <p className="text-xs text-blue-700/80 leading-relaxed">
                            此处仅统计已经【完成】或【终止】项目的实际结算金额。
                            <br />
                            1. 对于已完成的项目，金额为该项目的合同总额（无论是一次付清还是分期）。
                            <br />
                            2. 对于中途终止的项目，金额仅包含已支付并结算的首付款（或其他阶段款项），后续未支付款项不计入。
                        </p>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default TransactionsPage;
